import ReactMarkdown from 'react-markdown';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { ScrollArea } from '../ui/scroll-area';
import { Loader2, Copy, Check } from 'lucide-react';
import { type Campaign } from './types';

interface CampaignBriefDialogProps {
    isOpen: boolean;
    onClose: () => void;
    campaign: Campaign | null;
    briefContent: string;
    briefPdfUrl: string | null;
    briefError: string | null;
    isGenerating: boolean;
    isCopied: boolean;
    onCopy: () => void;
}

export function CampaignBriefDialog({
    isOpen,
    onClose,
    campaign,
    briefContent,
    briefPdfUrl,
    briefError,
    isGenerating,
    isCopied,
    onCopy
}: CampaignBriefDialogProps) {
    return (
        <Dialog
            open={isOpen}
            onOpenChange={(open) => {
                if (!open) onClose();
            }}
        >
            <DialogContent className="max-w-5xl w-[95vw] max-h-[85vh] overflow-hidden">
                <DialogHeader>
                    <DialogTitle>
                        Campaign Brief{campaign ? `: ${campaign.name}` : ''}
                    </DialogTitle>
                    <DialogDescription>
                        {campaign
                            ? `Generated for ${campaign.businessUnit} • ${campaign.category}`
                            : 'Agency-ready campaign briefing generated by SmartAgents.'}
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                        <p className="text-sm text-gray-600">
                            {isGenerating
                                ? 'Generating campaign briefing with SmartAgents...'
                                : briefError
                                    ? 'We could not generate the briefing.'
                                    : 'Copy and share this briefing with your agency partners.'}
                        </p>
                        <div className="flex items-center gap-2">
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                    if (!briefPdfUrl) {
                                        return;
                                    }

                                    window.open(briefPdfUrl, '_blank', 'noopener,noreferrer');
                                }}
                                disabled={isGenerating || !briefPdfUrl}
                            >
                                Download PDF
                            </Button>
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={onCopy}
                                disabled={!briefContent || isGenerating}
                                className="flex items-center gap-2"
                            >
                                {isCopied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                                {isCopied ? 'Copied' : 'Copy brief'}
                            </Button>
                        </div>
                    </div>
                    <div className="rounded-md border bg-white">
                        <ScrollArea className="h-[60vh] p-4">
                            {isGenerating ? (
                                <div className="flex items-center gap-2 text-sm text-gray-600">
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    Generating campaign brief...
                                </div>
                            ) : briefError ? (
                                <div className="text-sm text-red-600">{briefError}</div>
                            ) : (
                                <div className="prose prose-sm max-w-none">
                                    <ReactMarkdown>{briefContent}</ReactMarkdown>
                                </div>
                            )}
                        </ScrollArea>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
